import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import PasswortSetzen from "@/pages/PasswortSetzen";
import PasswortVergessen from "@/pages/PasswortVergessen";
import PasswortZuruecksetzen from "@/pages/PasswortZuruecksetzen";
import Dashboard from "@/pages/Dashboard";
import Assets from "@/pages/Assets";
import Maintenance from "@/pages/Maintenance";
import Inspections from "@/pages/Inspections";
import Documents from "@/pages/Documents";
import Audit from "@/pages/Audit";
import Admin from "@/pages/Admin";
import ServiceAI from "@/pages/ServiceAI";
import Service from "@/pages/Service";
import Repairs from "@/pages/Repairs";
import Notdienst from "@/pages/Notdienst";
import Material from "@/pages/Material";
import Lager from "@/pages/Lager";
import Media from "@/pages/Media";
import Sync from "@/pages/Sync";
import Contracts from "@/pages/Contracts";
import CRM from "@/pages/CRM";
import Portal from "@/pages/Portal";
import Rules from "@/pages/Rules";
import Standorte from "@/pages/Standorte";
import StandortDetail from "@/pages/StandortDetail";
import AnlageAkte from "@/pages/AnlageAkte";
import Wartungsauftraege from "@/pages/Wartungsauftraege";
import WartungsauftragDetail from "@/pages/WartungsauftragDetail";
import Pruefpunkte from "@/pages/Pruefpunkte";
import CmsAbnahme from "@/pages/CmsAbnahme";
import DigitalTwin from "@/pages/DigitalTwin";
import Kennzahlen from "@/pages/Kennzahlen";
import CmsEditor from "@/pages/CmsEditor";
import CmsEditorObject from "@/pages/CmsEditorObject";
import Stammdaten from "@/pages/Stammdaten";
import Plantafel from "@/pages/Plantafel";
import Kalender from "@/pages/Kalender";
import Analytics from "@/pages/Analytics";
import Benachrichtigungen from "@/pages/Benachrichtigungen";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-muted-foreground">Lädt…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.needs_password_change) return <Navigate to="/passwort-setzen" replace />;
  return <Layout>{children}</Layout>;
}

function SetPwGuard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-muted-foreground">Lädt…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.needs_password_change) return <Navigate to="/" replace />;
  return <PasswortSetzen />;
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/passwort-vergessen" element={<PasswortVergessen />} />
            <Route path="/passwort-zuruecksetzen" element={<PasswortZuruecksetzen />} />
            <Route path="/passwort-setzen" element={<SetPwGuard />} />
            <Route path="/" element={<Protected><Dashboard /></Protected>} />
            <Route path="/kennzahlen" element={<Protected><Kennzahlen /></Protected>} />
            <Route path="/plantafel" element={<Protected><Plantafel /></Protected>} />
            <Route path="/kalender" element={<Protected><Kalender /></Protected>} />
            <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
            <Route path="/benachrichtigungen" element={<Protected><Benachrichtigungen /></Protected>} />
            <Route path="/anlagen" element={<Protected><Assets /></Protected>} />
            <Route path="/standorte" element={<Protected><Standorte /></Protected>} />
            <Route path="/standorte/:id" element={<Protected><StandortDetail /></Protected>} />
            <Route path="/anlage/:id" element={<Protected><AnlageAkte /></Protected>} />
            <Route path="/wartungsauftraege" element={<Protected><Wartungsauftraege /></Protected>} />
            <Route path="/wartungsauftraege/:id" element={<Protected><WartungsauftragDetail /></Protected>} />
            <Route path="/pruefpunkte" element={<Protected><Pruefpunkte /></Protected>} />
            <Route path="/cms" element={<Protected><CmsAbnahme /></Protected>} />
            <Route path="/cms-editor" element={<Protected><CmsEditor /></Protected>} />
            <Route path="/cms-editor/:library/:key" element={<Protected><CmsEditorObject /></Protected>} />
            <Route path="/digital-twin" element={<Protected><DigitalTwin /></Protected>} />
            <Route path="/stammdaten" element={<Protected><Stammdaten /></Protected>} />
            <Route path="/anlagen/:id" element={<Protected><AnlageAkte /></Protected>} />
            <Route path="/wartung" element={<Protected><Maintenance /></Protected>} />
            <Route path="/pruefungen" element={<Protected><Inspections /></Protected>} />
            <Route path="/service" element={<Protected><Service /></Protected>} />
            <Route path="/reparaturen" element={<Protected><Repairs /></Protected>} />
            <Route path="/notdienst" element={<Protected><Notdienst /></Protected>} />
            <Route path="/vertraege" element={<Protected><Contracts /></Protected>} />
            <Route path="/kunden" element={<Protected><CRM /></Protected>} />
            <Route path="/kundenportal" element={<Protected><Portal /></Protected>} />
            <Route path="/regelwerk" element={<Protected><Rules /></Protected>} />
            <Route path="/material" element={<Protected><Material /></Protected>} />
            <Route path="/lager" element={<Protected><Lager /></Protected>} />
            <Route path="/medien" element={<Protected><Media /></Protected>} />
            <Route path="/sync" element={<Protected><Sync /></Protected>} />
            <Route path="/ki-analyse" element={<Protected><ServiceAI /></Protected>} />
            <Route path="/dokumente" element={<Protected><Documents /></Protected>} />
            <Route path="/audit" element={<Protected><Audit /></Protected>} />
            <Route path="/verwaltung" element={<Protected><Admin /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
